import { currentLanguage } from "./locale";

export function isSpanishLocale() {
  return currentLanguage() === "es";
}

export function localizedPath(path) {
  if (!isSpanishLocale()) return path;
  if (!path || path === "/") return "/es";
  if (path.startsWith("/es")) return path;
  if (path.startsWith("#")) return path;
  return `/${["es", path.replace(/^\/+/, "")].join("/")}`;
}

export const SUIT_NAMES_ES = {
  "Major Arcana": "Arcanos Mayores",
  Wands: "Bastos",
  Cups: "Copas",
  Swords: "Espadas",
  Pentacles: "Oros",
};

const MAJOR_NAMES_ES = {
  "The Fool": "El Loco",
  "The Magician": "El Mago",
  "The High Priestess": "La Sacerdotisa",
  "The Empress": "La Emperatriz",
  "The Emperor": "El Emperador",
  "The Hierophant": "El Hierofante",
  "The Lovers": "Los Enamorados",
  "The Chariot": "El Carro",
  Strength: "La Fuerza",
  "The Hermit": "El Ermitaño",
  "Wheel of Fortune": "La Rueda de la Fortuna",
  Justice: "La Justicia",
  "The Hanged Man": "El Colgado",
  Death: "La Muerte",
  Temperance: "La Templanza",
  "The Devil": "El Diablo",
  "The Tower": "La Torre",
  "The Star": "La Estrella",
  "The Moon": "La Luna",
  "The Sun": "El Sol",
  Judgement: "El Juicio",
  "The World": "El Mundo",
};

const RANK_NAMES_ES = {
  Ace: "As",
  Two: "Dos",
  Three: "Tres",
  Four: "Cuatro",
  Five: "Cinco",
  Six: "Seis",
  Seven: "Siete",
  Eight: "Ocho",
  Nine: "Nueve",
  Ten: "Diez",
  Page: "Sota",
  Knight: "Caballero",
  Queen: "Reina",
  King: "Rey",
};

const MINOR_SUITS_ES = {
  Wands: "Bastos",
  Cups: "Copas",
  Swords: "Espadas",
  Pentacles: "Oros",
};

export function translateSuitName(suit) {
  if (!isSpanishLocale()) return suit;
  return SUIT_NAMES_ES[suit] || suit;
}

export function translateCardName(name = "") {
  if (!isSpanishLocale()) return name;
  if (MAJOR_NAMES_ES[name]) return MAJOR_NAMES_ES[name];

  const match = String(name).match(
    /^(Ace|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten|Page|Knight|Queen|King) of (Wands|Cups|Swords|Pentacles)$/
  );

  if (!match) return name;
  return `${RANK_NAMES_ES[match[1]]} de ${MINOR_SUITS_ES[match[2]]}`;
}

export function translateCardAlt(name = "") {
  if (!isSpanishLocale()) return `${name} tarot card`;
  return `Carta de tarot ${translateCardName(name)}`;
}

export function translateEmotionLabel(label = "") {
  if (!isSpanishLocale()) return label;
  const value = String(label).toLowerCase();
  const map = {
    abandoned: "abandonada",
    anxious: "ansiosa",
    confused: "confundida",
    heartbroken: "con el corazón roto",
    hopeful: "esperanzada",
    jealous: "celosa",
    lonely: "sola",
    overwhelmed: "abrumada",
    regretful: "arrepentida",
    restless: "inquieta",
    uncertain: "incierta",
    unloved: "poco amada",
  };
  return map[value] || label;
}

const KEYWORD_ES = {
  action: "acción",
  adventure: "aventura",
  abundance: "abundancia",
  alignment: "alineación",
  ambition: "ambición",
  anxiety: "ansiedad",
  attainment: "logro",
  awakening: "despertar",
  balance: "equilibrio",
  beginnings: "comienzos",
  burden: "carga",
  celebration: "celebración",
  change: "cambio",
  challenge: "desafío",
  choice: "elección",
  choices: "opciones",
  clarity: "claridad",
  compassion: "compasión",
  completion: "cierre",
  confidence: "confianza",
  conflict: "conflicto",
  connection: "conexión",
  contemplation: "contemplación",
  courage: "valor",
  creativity: "creatividad",
  creation: "creación",
  departure: "partida",
  disappointment: "decepción",
  discontent: "descontento",
  devotion: "devoción",
  dreams: "sueños",
  discipline: "disciplina",
  emotional: "emocional",
  emotion: "emoción",
  endings: "cierres",
  faith: "fe",
  fear: "miedo",
  focus: "enfoque",
  growth: "crecimiento",
  healing: "sanación",
  hope: "esperanza",
  illusions: "ilusiones",
  innocence: "inocencia",
  intuition: "intuición",
  joy: "alegría",
  leadership: "liderazgo",
  loss: "pérdida",
  love: "amor",
  manifestation: "manifestación",
  mutual: "mutuo",
  movement: "movimiento",
  nostalgia: "nostalgia",
  patience: "paciencia",
  potential: "potencial",
  purpose: "propósito",
  reflection: "reflexión",
  renewal: "renovación",
  rest: "descanso",
  shared: "compartido",
  sisterhood: "hermandad",
  soulful: "profundo",
  spirit: "espíritu",
  security: "seguridad",
  stability: "estabilidad",
  strength: "fuerza",
  surrender: "entrega",
  transition: "transición",
  transformation: "transformación",
  truth: "verdad",
  union: "unión",
  unity: "unidad",
  victory: "victoria",
  vision: "visión",
  willpower: "voluntad",
  wisdom: "sabiduría",
};

const PHRASE_ES = {
  "abundance, generosity, community": "Abundancia, generosidad, comunidad",
  "achievement, fulfillment, solitude": "Logro, plenitud, independencia",
  "ambition, study, grounded dreams": "Ambicion, estudio, suenos con base real",
  "anxiety, nightmares, inner demons": "Ansiedad, pesadillas, sombras internas",
  "authority, structure, control": "Autoridad, estructura, control",
  "awakening, redemption, higher calling": "Despertar, redencion, llamado superior",
  "balance, harmony, the timing of life": "Equilibrio, armonia, ritmo de la vida",
  "balance, priorities, juggling responsibilities": "Equilibrio, prioridades, responsabilidades en movimiento",
  "balance, choices, multitasking": "Equilibrio, elecciones, adaptacion",
  "burden, overload, responsibility": "Carga, exceso, responsabilidad",
  "celebration, stability, milestones": "Celebracion, estabilidad, logros importantes",
  "celebration, sisterhood, shared joy": "Celebracion, hermandad, alegria compartida",
  "choices, illusions, dreams": "Opciones, ilusiones, suenos",
  "clarity, breakthroughs, mental power": "Claridad, revelaciones, poder mental",
  "compassion, intuition, emotional depth": "Compasion, intuicion, profundidad emocional",
  "completion, wholeness, integration": "Cierre, plenitud, integracion",
  "completion, wholeness, fulfillment": "Cierre, plenitud, realizacion",
  "confidence, radiance, inner flame": "Confianza, brillo, fuego interior",
  "conflict, ego battles, hollow victory": "Conflicto, luchas de ego, victoria vacia",
  "conflict, competition, tension": "Conflicto, competencia, tension",
  "confusion, choices, illusion": "Confusion, opciones, ilusion",
  "contentment, gratitude, fulfillment": "Contento, gratitud, plenitud",
  "contentment, gratitude, wishes fulfilled": "Contento, gratitud, deseos cumplidos",
  "control, possession, holding on": "Control, posesion, apego",
  "creative innocence, emotional curiosity, messages of the heart": "Inocencia creativa, curiosidad emocional, mensajes del corazon",
  "curiosity, truth seeking, mental clarity": "Curiosidad, busqueda de verdad, claridad mental",
  "cycles, destiny, turning points": "Ciclos, destino, puntos de giro",
  "deception, strategy, hidden motives": "Engano, estrategia, motivos ocultos",
  "defense, standing ground, perseverance": "Defensa, firmeza, perseverancia",
  "defense, perseverance, challenge": "Defensa, perseverancia, desafio",
  "departure, transition, emotional growth": "Partida, transicion, crecimiento emocional",
  "determination, bold action, unstoppable drive": "Determinacion, accion valiente, impulso imparable",
  "diligence, responsibility, steady progress": "Diligencia, responsabilidad, progreso constante",
  "discontent, contemplation, emotional withdrawal": "Descontento, contemplacion, retiro emocional",
  "drive, passion, bold pursuit": "Impulso, pasion, busqueda valiente",
  "emotional beginnings, intuition, new love": "Comienzos emocionales, intuicion, nuevo amor",
  "emotional harmony, unity, love fulfilled": "Armonia emocional, unidad, amor realizado",
  "emotional harmony, unity, family": "Armonia emocional, union, familia",
  "emotional mastery, wisdom, calm authority": "Maestria emocional, sabiduria, autoridad serena",
  "emotional renewal, love, overflowing spirit": "Renovacion emocional, amor, espiritu abierto",
  "endings, betrayal, collapse": "Finales, traicion, colapso",
  "endings, betrayal, release": "Finales, traicion, liberacion",
  "endings, transformation, renewal": "Finales, transformacion, renovacion",
  "fairness, truth, law": "Equidad, verdad, justicia",
  "fertility, nurturing, abundance": "Fertilidad, cuidado, abundancia",
  "fertility, abundance, creation": "Fertilidad, abundancia, creacion",
  "generosity, reciprocity, financial balance": "Generosidad, reciprocidad, equilibrio financiero",
  "grief, disappointment, emotional loss": "Duelo, decepcion, perdida emocional",
  "hardship, loss, spiritual poverty": "Dificultad, perdida, pobreza espiritual",
  "heartbreak, grief, emotional pain": "Ruptura, duelo, dolor emocional",
  "heartbreak, sorrow, truth": "Dolor del corazon, tristeza, verdad",
  "hope, renewal, deep light": "Esperanza, renovacion, luz profunda",
  "illusion, intuition, shadow work": "Ilusion, intuicion, trabajo con la sombra",
  "illusion, fear, subconscious": "Ilusion, miedo, subconsciente",
  "indecision, inner conflict, impasse": "Indecision, conflicto interno, bloqueo",
  "independence, luxury, self worth": "Independencia, bienestar, valor propio",
  "inner power, compassion, resilience": "Poder interior, compasion, resiliencia",
  "inner strength, courage, compassion": "Fuerza interior, valor, compasion",
  "inspiration, discovery, enthusiasm": "Inspiracion, descubrimiento, entusiasmo",
  "inspiration, passion, spark of action": "Inspiracion, pasion, chispa de accion",
  "introspection, solitude, inner guidance": "Introspeccion, soledad, guia interior",
  "isolation, loss, hardship": "Aislamiento, perdida, dificultad",
  "joy, clarity, radiance": "Alegria, claridad, brillo",
  "joy, success, vitality": "Alegria, exito, vitalidad",
  "legacy, stability, generational wealth": "Legado, estabilidad, riqueza generacional",
  "manifestation, willpower, creation": "Manifestacion, voluntad, creacion",
  "mastery, craft, dedication to growth": "Maestria, oficio, dedicacion al crecimiento",
  "mastery, wealth, leadership with integrity": "Maestria, riqueza, liderazgo con integridad",
  "meditation, rest, recovery": "Meditacion, descanso, recuperacion",
  "mental mastery, truth, authority": "Maestria mental, verdad, autoridad",
  "momentum, swift change, forward motion": "Impulso, cambio rapido, avance",
  "movement, change, transition": "Movimiento, cambio, transicion",
  "mystery, intuition, inner knowledge": "Misterio, intuicion, conocimiento interior",
  "new beginnings, innocence, leap of faith": "Nuevos comienzos, inocencia, salto de fe",
  "nostalgia, innocence, emotional roots": "Nostalgia, inocencia, raices emocionales",
  "nurturing, prosperity, grounded balance": "Cuidado, prosperidad, equilibrio con base real",
  "patience, balance, healing": "Paciencia, equilibrio, sanacion",
  "patience, investment, long term growth": "Paciencia, inversion, crecimiento a largo plazo",
  "planning, discovery, ambition": "Planificacion, descubrimiento, ambicion",
  "progress, expansion, foresight": "Progreso, expansion, vision futura",
  "prosperity, foundations, material blessings": "Prosperidad, bases solidas, bendiciones materiales",
  "rapid movement, messages, momentum": "Movimiento rapido, mensajes, impulso",
  "resilience, wounded warrior, last stand": "Resiliencia, guerrero herido, ultima resistencia",
  "rest, reflection, recuperation": "Descanso, reflexion, recuperacion",
  "restriction, limiting beliefs, mental trap": "Restriccion, creencias limitantes, trampa mental",
  "revelation, collapse, awakening": "Revelacion, colapso, despertar",
  "romantic pursuit, artistic vision, emotional action": "Busqueda romantica, vision artistica, accion emocional",
  "sacrifice, pause, new perspective": "Sacrificio, pausa, nueva perspectiva",
  "skill, diligence, mastery": "Habilidad, constancia, maestria",
  "solitude, wisdom, inner guidance": "Soledad, sabiduria, guia interior",
  "soulmate, harmony, relationships": "Alma afin, armonia, relaciones",
  "stability, celebration, homecoming": "Estabilidad, celebracion, regreso al hogar",
  "strategy, deception, self preservation": "Estrategia, engano, autoproteccion",
  "structure, authority, stability": "Estructura, autoridad, estabilidad",
  "surrender, perspective, pause": "Entrega, perspectiva, pausa",
  "teamwork, mastery, shared goals": "Trabajo en equipo, maestria, metas compartidas",
  "temptation, attachment, shadow work": "Tentacion, apego, trabajo con la sombra",
  "temptation, shadow, bondage": "Tentacion, sombra, atadura",
  "tradition, wisdom, spiritual authority": "Tradicion, sabiduria, autoridad espiritual",
  "tradition, teaching, spiritual wisdom": "Tradicion, ensenanza, sabiduria espiritual",
  "transformation, endings, rebirth": "Transformacion, finales, renacimiento",
  "transition, recovery, letting go": "Transicion, recuperacion, soltar",
  "truth, fairness, accountability": "Verdad, equidad, responsabilidad",
  "truth, independence, clear boundaries": "Verdad, independencia, limites claros",
  "union, choice, deep alignment": "Union, eleccion, alineacion profunda",
  "union, mutual love, soulful connection": "Union, amor mutuo, conexion profunda",
  "victory, recognition, achievement": "Victoria, reconocimiento, logro",
  "victory, recognition, confidence": "Victoria, reconocimiento, confianza",
  "vision, authority, mastery of fire": "Vision, autoridad, maestria del fuego",
  "vision, decisions, exploration": "Vision, decisiones, exploracion",
  "vision, leadership, decision": "Vision, liderazgo, decision",
  "willpower, triumph, direction": "Voluntad, triunfo, direccion",
};

export function translateKeywordPhrase(value = "") {
  if (!isSpanishLocale()) return value;
  const phrase = String(value).replace(/\s+/g, " ").trim();
  const phraseKey = phrase.toLowerCase();
  if (PHRASE_ES[phraseKey]) return PHRASE_ES[phraseKey];

  const extraWords = {
    action: "accion",
    attachment: "apego",
    authority: "autoridad",
    battles: "luchas",
    betrayal: "traicion",
    blessings: "bendiciones",
    bold: "valiente",
    boundaries: "limites",
    breakthroughs: "revelaciones",
    calling: "llamado",
    collapse: "colapso",
    control: "control",
    craft: "oficio",
    creative: "creativa",
    curiosity: "curiosidad",
    cycles: "ciclos",
    decisions: "decisiones",
    dedication: "dedicacion",
    deep: "profundo",
    destiny: "destino",
    determination: "determinacion",
    diligence: "diligencia",
    direction: "direccion",
    discovery: "descubrimiento",
    drive: "impulso",
    ego: "ego",
    enthusiasm: "entusiasmo",
    expansion: "expansion",
    exploration: "exploracion",
    fairness: "equidad",
    financial: "financiero",
    fire: "fuego",
    flame: "llama",
    foresight: "vision futura",
    foundations: "bases",
    fulfilled: "realizado",
    fulfillment: "plenitud",
    generosity: "generosidad",
    generational: "generacional",
    grounded: "con base real",
    guidance: "guia",
    hardship: "dificultad",
    heart: "corazon",
    hollow: "vacia",
    impasse: "bloqueo",
    independence: "independencia",
    integration: "integracion",
    integrity: "integridad",
    inner: "interior",
    introspection: "introspeccion",
    investment: "inversion",
    knowledge: "conocimiento",
    legacy: "legado",
    light: "luz",
    limiting: "limitantes",
    luxury: "bienestar",
    mastery: "maestria",
    material: "materiales",
    mental: "mental",
    messages: "mensajes",
    momentum: "impulso",
    mystery: "misterio",
    nightmares: "pesadillas",
    nurturing: "cuidado",
    overload: "exceso",
    pain: "dolor",
    passion: "pasion",
    pause: "pausa",
    perseverance: "perseverancia",
    perspective: "perspectiva",
    possession: "posesion",
    poverty: "pobreza",
    preservation: "proteccion",
    priorities: "prioridades",
    progress: "progreso",
    prosperity: "prosperidad",
    pursuit: "busqueda",
    radiance: "brillo",
    recuperation: "recuperacion",
    reciprocity: "reciprocidad",
    redemption: "redencion",
    resilience: "resiliencia",
    responsibility: "responsabilidad",
    restriction: "restriccion",
    revelation: "revelacion",
    romantic: "romantica",
    spark: "chispa",
    stand: "resistencia",
    steady: "constante",
    strategy: "estrategia",
    structure: "estructura",
    study: "estudio",
    swift: "rapido",
    teamwork: "trabajo en equipo",
    timing: "ritmo",
    trap: "trampa",
    unstoppable: "imparable",
    warrior: "guerrero",
    wealth: "riqueza",
    work: "trabajo",
    worth: "valor",
  };

  const translateLooseWords = (text) =>
    String(text)
      .split(/\s+/)
      .map((word) => {
        const match = word.match(/^([A-Za-z]+)([^A-Za-z]*)$/);
        if (!match) return word;
        const translated = KEYWORD_ES[match[1].toLowerCase()] || extraWords[match[1].toLowerCase()];
        return translated ? `${translated}${match[2] || ""}` : word;
      })
      .join(" ");

  return String(value)
    .split(",")
    .map((part) => {
      const clean = part.trim();
      const key = clean.toLowerCase();
      return KEYWORD_ES[key] || extraWords[key] || translateLooseWords(clean);
    })
    .filter(Boolean)
    .join(", ");
}

export function localizeMeaningLine(value = "") {
  if (!isSpanishLocale()) return value;
  const text = String(value).replace(/\s+/g, " ").trim();
  const colonIndex = text.indexOf(":");
  if (colonIndex < 0) return text;

  const label = text.slice(0, colonIndex).trim();
  const detail = text.slice(colonIndex + 1).trim();
  return `${translateKeywordPhrase(label)}: ${detail}`;
}

const MEANING_LABEL_ES = {
  "many options": "Muchas opciones",
  illusion: "Ilusión",
  "need for discernment": "Necesidad de discernimiento",
  "overwhelm by choice": "Saturación por elegir",
  "vision before commitment": "Visión antes del compromiso",
  "clarity returning": "Claridad que regresa",
  "poor choices exposed": "Malas elecciones a la vista",
  "decision needed": "Decisión necesaria",
  "escapism ending or worsening": "Evasión que termina o se intensifica",
  "grounded desire": "Deseo con los pies en la tierra",
  "new beginnings": "Nuevos comienzos",
  spontaneity: "Espontaneidad",
  innocence: "Inocencia",
  faith: "Fe",
  potential: "Potencial",
  recklessness: "Impulsividad",
  naivety: "Ingenuidad",
  "fear of the unknown": "Miedo a lo desconocido",
  disconnection: "Desconexión",
  "missed opportunities": "Oportunidades perdidas",
  manifestation: "Manifestación",
  focus: "Enfoque",
  skill: "Habilidad",
  confidence: "Confianza",
  manipulation: "Manipulación",
  "blocked energy": "Energía bloqueada",
  intuition: "Intuición",
  mystery: "Misterio",
  stillness: "Quietud",
  secrecy: "Secreto",
  fertility: "Fertilidad",
  abundance: "Abundancia",
  creativity: "Creatividad",
  nurture: "Cuidado",
  authority: "Autoridad",
  structure: "Estructura",
  discipline: "Disciplina",
  tradition: "Tradición",
  teaching: "Enseñanza",
  commitment: "Compromiso",
  love: "Amor",
  harmony: "Armonía",
  choice: "Elección",
  movement: "Movimiento",
  willpower: "Voluntad",
  victory: "Victoria",
  strength: "Fuerza",
  courage: "Valor",
  compassion: "Compasión",
  solitude: "Soledad",
  wisdom: "Sabiduría",
  change: "Cambio",
  karma: "Karma",
  fairness: "Equidad",
  truth: "Verdad",
  pause: "Pausa",
  surrender: "Entrega",
  transformation: "Transformación",
  endings: "Cierres",
  balance: "Equilibrio",
  patience: "Paciencia",
  temptation: "Tentación",
  shadow: "Sombra",
  upheaval: "Sacudida",
  hope: "Esperanza",
  healing: "Sanación",
  fear: "Miedo",
  success: "Éxito",
  awakening: "Despertar",
  completion: "Cierre",
};

const MEANING_WORD_ES = {
  action: "acción",
  alignment: "alineación",
  ambition: "ambición",
  anxiety: "ansiedad",
  attachment: "apego",
  balance: "equilibrio",
  beginning: "comienzo",
  beginnings: "comienzos",
  blocked: "bloqueada",
  burden: "carga",
  challenge: "desafío",
  choice: "elección",
  choices: "elecciones",
  clarity: "claridad",
  commitment: "compromiso",
  communication: "comunicación",
  conflict: "conflicto",
  connection: "conexión",
  courage: "valor",
  decision: "decisión",
  desire: "deseo",
  discernment: "discernimiento",
  discipline: "disciplina",
  emotional: "emocional",
  endings: "cierres",
  energy: "energía",
  exposed: "expuestas",
  faith: "fe",
  fear: "miedo",
  grounded: "concreto",
  healing: "sanación",
  illusion: "ilusión",
  inner: "interior",
  love: "amor",
  many: "muchas",
  movement: "movimiento",
  needed: "necesaria",
  need: "necesidad",
  options: "opciones",
  overwhelm: "saturación",
  patience: "paciencia",
  poor: "malas",
  potential: "potencial",
  returning: "que regresa",
  reversed: "invertido",
  shadow: "sombra",
  stability: "estabilidad",
  truth: "verdad",
  vision: "visión",
};

const splitMeaningParts = (value = "") => {
  const text = String(value).replace(/\s+/g, " ").trim();
  const colonIndex = text.indexOf(":");
  if (colonIndex < 0) return [text, ""];
  return [text.slice(0, colonIndex).trim(), text.slice(colonIndex + 1).trim()];
};

const translateMeaningLabel = (label = "") => {
  const clean = String(label).replace(/\s+/g, " ").trim();
  const key = clean.toLowerCase();
  if (MEANING_LABEL_ES[key]) return MEANING_LABEL_ES[key];
  if (PHRASE_ES[key]) return PHRASE_ES[key];
  if (KEYWORD_ES[key]) return KEYWORD_ES[key].replace(/^\w/, (char) => char.toUpperCase());

  return clean
    .split(/\s+/)
    .map((word) => MEANING_WORD_ES[word.toLowerCase()] || KEYWORD_ES[word.toLowerCase()] || word)
    .join(" ")
    .replace(/^\w/, (char) => char.toUpperCase());
};

const meaningDescriptionByLabel = (card, orientation, rawLabel, index) => {
  const name = translateCardName(card?.name);
  const theme = translateKeywordPhrase(card?.subtitle || "").toLowerCase();
  const key = String(rawLabel).toLowerCase();

  const exact = {
    "many options": `${name} muestra imaginación, deseo y varias posibilidades abiertas. No todas tienen la misma base real.`,
    illusion: "La carta advierte que la fantasía, el miedo o el deseo pueden hacer que una opción parezca mejor de lo que es.",
    "need for discernment": "No todo camino tiene el mismo valor, costo o verdad. Conviene mirar con calma antes de elegir.",
    "overwhelm by choice": "Demasiadas posibilidades pueden frenar la acción. La lectura pide simplificar y elegir lo esencial.",
    "vision before commitment": "Antes de comprometerte, separa el sueño de una decisión que realmente puedas sostener.",
    "clarity returning": `${name} invertida muestra opciones que empiezan a reducirse y una realidad más fácil de reconocer.`,
    "poor choices exposed": "Una fantasía puede perder brillo cuando aparecen los hechos. La carta ayuda a ver qué no se sostiene.",
    "decision needed": "La lectura pide compromiso y una elección concreta, no seguir imaginando posibilidades sin fin.",
    "escapism ending or worsening": "La inversión puede mostrar un despertar de la ilusión o una tendencia a esconderse más dentro de ella.",
    "grounded desire": "Favorece elegir lo que puede vivirse de verdad, con deseo, claridad y responsabilidad.",
  };

  if (exact[key]) return exact[key];

  const uprightFallbacks = [
    `${name} señala una energía de ${theme || "claridad interior"} que puede ayudarte a entender mejor la situación.`,
    "La carta invita a observar lo que está creciendo, lo que pide atención y lo que necesita una elección más consciente.",
    "En una lectura, este punto muestra una oportunidad de actuar con más presencia y menos impulso.",
    "El mensaje pide escuchar la emoción sin dejar que ella tome toda la decisión.",
    "Llévalo a algo práctico: una conversación, un límite, una pausa o un paso pequeño que puedas sostener.",
  ];

  const reversedFallbacks = [
    `${name} invertida señala una zona de bloqueo, exceso o confusión alrededor de ${theme || "la situación actual"}.`,
    "La carta pide mirar lo que se evita, se exagera o se repite sin darte verdadera claridad.",
    "Puede mostrar que la energía está volviéndose hacia dentro y necesita orden antes de actuar.",
    "La lectura recomienda bajar el ritmo, revisar los hechos y no decidir desde presión emocional.",
    "El consejo es recuperar centro: nombra lo que sabes, lo que sientes y el siguiente paso realista.",
  ];

  return (orientation === "reversed" ? reversedFallbacks : uprightFallbacks)[index % 5];
};

export function spanishMeaningLines(card, orientation = "upright") {
  const source = orientation === "reversed" ? card?.reversed : card?.upright;
  return (source || []).map((item, index) => {
    const [rawLabel] = splitMeaningParts(item);
    const label = translateMeaningLabel(rawLabel);
    return `${label}: ${meaningDescriptionByLabel(card, orientation, rawLabel, index)}`;
  });
}

const SYMBOL_WORD_ES = {
  abandoned: "abandonadas",
  acolytes: "acolitos",
  archetypal: "arquetipica",
  arch: "arco",
  archway: "arco",
  altar: "altar",
  among: "entre",
  angel: "angel",
  arranged: "ordenadas",
  arms: "brazos",
  armor: "armadura",
  at: "en",
  back: "espalda",
  background: "fondo",
  bandaged: "vendada",
  banner: "estandarte",
  bare: "descalzos",
  bed: "cama",
  behind: "detras",
  below: "debajo",
  bent: "inclinada",
  beyond: "mas alla",
  bird: "ave",
  birds: "aves",
  black: "negro",
  blue: "azul",
  boat: "barca",
  bound: "atada",
  breaking: "quebrada",
  bridge: "puente",
  bright: "brillante",
  bull: "toro",
  bundled: "agrupados",
  butterflies: "mariposas",
  caduceus: "caduceo",
  camp: "campamento",
  canopy: "dosel",
  carved: "tallado",
  carrying: "cargando",
  castle: "castillo",
  cat: "gato",
  cathedral: "catedral",
  central: "central",
  chains: "cadenas",
  charging: "avanzando",
  cherub: "querubin",
  child: "nino",
  city: "ciudad",
  clear: "despejado",
  cloak: "capa",
  closed: "cerrada",
  collected: "recogidas",
  companion: "companero",
  covered: "cubierto",
  craftsperson: "artesano",
  crayfish: "cangrejo",
  crossing: "cruzando",
  crossed: "cruzadas",
  crowd: "multitud",
  crutch: "muleta",
  dancer: "bailarina",
  dancing: "danzantes",
  dark: "oscuro",
  dawn: "amanecer",
  de: "de",
  desert: "desierto",
  distance: "distancia",
  distant: "distante",
  dog: "perro",
  dogs: "perros",
  dove: "paloma",
  elder: "anciano",
  exchanged: "intercambiadas",
  falcon: "halcon",
  fallen: "caida",
  falling: "cayendo",
  family: "familia",
  far: "lejana",
  feet: "pies",
  field: "campo",
  fields: "campos",
  figure: "figura",
  figures: "figuras",
  filled: "llenas",
  finished: "terminados",
  fish: "pez",
  fixed: "fijo",
  flight: "vuelo",
  flower: "flor",
  flowered: "florida",
  following: "siguiendo",
  foot: "pie",
  forward: "hacia adelante",
  from: "desde",
  gap: "espacio",
  garden: "jardin",
  garlanded: "adornados",
  gentle: "suaves",
  given: "entregadas",
  glass: "vidrio",
  globe: "globo",
  grapevine: "vid",
  grapevines: "vides",
  green: "verde",
  ground: "suelo",
  guardians: "guardianes",
  halo: "halo",
  hand: "mano",
  hands: "manos",
  harvest: "cosecha",
  head: "cabeza",
  heads: "cabezas",
  height: "altura",
  held: "sostenida",
  helmet: "casco",
  her: "su",
  high: "alto",
  hills: "colinas",
  him: "el",
  horizon: "horizonte",
  horned: "con cuernos",
  image: "imagen",
  images: "imagenes",
  imagery: "imagineria",
  jagged: "irregulares",
  jars: "jarras",
  juggling: "malabarista",
  kneeling: "arrodilladas",
  landscape: "paisaje",
  lap: "regazo",
  laurel: "laurel",
  leader: "lider",
  leaning: "apoyado",
  letters: "letras",
  lightning: "rayo",
  lion: "leon",
  lions: "leones",
  living: "vivo",
  loop: "bucle",
  loose: "sueltas",
  lotus: "loto",
  wand: "basto",
  wands: "bastos",
  cup: "copa",
  cups: "copas",
  sword: "espada",
  swords: "espadas",
  pentacle: "oro",
  pentacles: "oros",
  meaning: "significado",
  moonlit: "iluminado por la luna",
  mountain: "montana",
  near: "cerca",
  no: "sin",
  observers: "observadores",
  of: "de",
  offering: "ofreciendo",
  on: "en",
  one: "un",
  open: "abierto",
  ornate: "ornamentada",
  overhead: "encima",
  overflowing: "desbordante",
  patterned: "estampada",
  pierced: "atravesado",
  planted: "plantados",
  plowed: "arados",
  posture: "postura",
  pyramids: "piramides",
  quilt: "colcha",
  quiet: "tranquilo",
  rabbit: "conejo",
  radiant: "radiante",
  rain: "lluvia",
  rainbow: "arcoiris",
  ram: "carnero",
  rearing: "erguido",
  red: "rojo",
  resting: "descansando",
  rider: "jinete",
  river: "rio",
  robe: "tunica",
  salamander: "salamandra",
  salamanders: "salamandras",
  scythe: "guadana",
  sea: "mar",
  seated: "sentada",
  serpent: "serpiente",
  sharp: "afiladas",
  shield: "escudo",
  ship: "barco",
  ships: "barcos",
  shore: "orilla",
  sky: "cielo",
  snail: "caracol",
  snow: "nieve",
  sphinx: "esfinge",
  sphinxes: "esfinges",
  spilled: "derramadas",
  staves: "varas",
  stained: "vitrales",
  still: "quieto",
  stone: "piedra",
  stormy: "tormentoso",
  sunflower: "girasol",
  sunflowers: "girasoles",
  suspended: "suspendida",
  them: "ellos",
  their: "sus",
  to: "hacia",
  tool: "herramienta",
  torch: "antorcha",
  towers: "torres",
  town: "pueblo",
  trumpet: "trompeta",
  tunic: "tunica",
  turned: "girada",
  turning: "giratoria",
  under: "bajo",
  uneven: "inestable",
  upright: "vertical",
  venus: "venus",
  view: "vista",
  viewer: "observador",
  walking: "alejandose",
  wall: "muro",
  waves: "olas",
  wheel: "rueda",
  windblown: "azotado por el viento",
  winged: "alado",
  wolf: "lobo",
  worker: "trabajador",
  workbench: "banco de trabajo",
  wreath: "corona",
  young: "jovenes",
  single: "un",
  two: "dos",
  three: "tres",
  four: "cuatro",
  five: "cinco",
  six: "seis",
  seven: "siete",
  eight: "ocho",
  nine: "nueve",
  ten: "diez",
  page: "sota",
  knight: "caballero",
  queen: "reina",
  king: "rey",
  with: "con",
  in: "en",
  a: "una",
  cloud: "nube",
  veiled: "velada",
  mixed: "mezclado",
  contents: "contenido",
  cliff: "precipicio",
  edge: "borde",
  small: "pequeno",
  pack: "bolsa",
  white: "blanco",
  rising: "naciente",
  sun: "sol",
  raised: "elevado",
  tools: "herramientas",
  infinity: "infinito",
  sign: "simbolo",
  flowering: "florecida",
  table: "mesa",
  twin: "dobles",
  pillars: "columnas",
  veil: "velo",
  scroll: "pergamino",
  moon: "luna",
  crown: "corona",
  wheat: "trigo",
  stars: "estrellas",
  flowing: "fluida",
  water: "agua",
  throne: "trono",
  mountains: "montanas",
  lantern: "linterna",
  staff: "baston",
  scales: "balanza",
  blindfold: "venda",
  snake: "serpiente",
  jewels: "joyas",
  tower: "torre",
  star: "estrella",
  path: "camino",
  flag: "bandera",
  coins: "monedas",
  tree: "arbol",
  life: "vida",
  pattern: "patron",
};

const SYMBOL_LABEL_ES = {
  "cups in a cloud": "Copas en una nube",
  "veiled figure": "Figura velada",
  "mixed contents": "Contenido mezclado",
  "single archetypal image": "Imagen arquetipica",
  "two archetypal images": "Dos imagenes arquetipicas",
  "three archetypal images": "Tres imagenes arquetipicas",
  "four archetypal images": "Cuatro imagenes arquetipicas",
  "five archetypal images": "Cinco imagenes arquetipicas",
  "six archetypal images": "Seis imagenes arquetipicas",
  "seven archetypal images": "Siete imagenes arquetipicas",
  "eight archetypal images": "Ocho imagenes arquetipicas",
  "nine archetypal images": "Nueve imagenes arquetipicas",
  "ten archetypal images": "Diez imagenes arquetipicas",
  "page with wand": "Sota con basto",
  "knight with wand": "Caballero con basto",
  "queen with wand": "Reina con basto",
  "king with wand": "Rey con basto",
  "page with cup": "Sota con copa",
  "knight with cup": "Caballero con copa",
  "queen with cup": "Reina con copa",
  "king with cup": "Rey con copa",
  "page with sword": "Sota con espada",
  "knight with sword": "Caballero con espada",
  "queen with sword": "Reina con espada",
  "king with sword": "Rey con espada",
  "page with pentacle": "Sota con oro",
  "knight with pentacle": "Caballero con oro",
  "queen with pentacle": "Reina con oro",
  "king with pentacle": "Rey con oro",
  "coins being given": "Monedas entregadas",
  "tree of life pattern": "Patron del Arbol de la Vida",
};

const SYMBOL_MEANING_ES = {
  "cups in a cloud|the cups appear as visions rather than objects on the ground, showing imagination and uncertainty.":
    "Las copas aparecen como visiones, no como objetos firmes en el suelo. Senalan imaginacion, deseo e incertidumbre emocional.",
  "veiled figure|the hidden figure in one cup suggests that not every desire is fully understood.":
    "La figura velada dentro de una copa sugiere que no todo deseo se entiende por completo. La carta pide mirar mas alla de la primera impresion.",
  "mixed contents|jewels, a snake, a wreath and other images show that choices can include both reward and danger.":
    "Las joyas, la serpiente, la corona y otras imagenes muestran que una eleccion puede traer recompensa, riesgo y fantasia al mismo tiempo.",
  "coins being given|the falling coins make generosity practical rather than symbolic.":
    "Las monedas entregadas convierten la generosidad en un acto concreto, no solo en una idea espiritual.",
  "tree of life pattern|the ten pentacles are arranged in a symbolic pattern, linking wealth with lineage and structure.":
    "El patron del Arbol de la Vida conecta la riqueza con linaje, estructura y continuidad familiar.",
};

const symbolKey = (value = "") => String(value).replace(/\s+/g, " ").trim().toLowerCase();

const translateSymbolWords = (label = "") =>
  String(label)
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((part) => {
      const clean = part.replace(/[^\w-]/g, "");
      const translated = SYMBOL_WORD_ES[clean.toLowerCase()];
      return translated || part;
    })
    .join(" ");

export function translateSymbolLabel(label = "") {
  if (!isSpanishLocale()) return label;
  const key = symbolKey(label);
  const exact = SYMBOL_LABEL_ES[key];
  if (exact) return exact;

  const generatedRank = key.match(/^(single|two|three|four|five|six|seven|eight|nine|ten) (wand|wands|cup|cups|sword|swords|pentacle|pentacles)$/);
  if (generatedRank) {
    return translateSymbolWords(key).replace(/^\w/, (char) => char.toUpperCase());
  }

  const generatedCourt = key.match(/^(page|knight|queen|king) with (wand|cup|sword|pentacle)$/);
  if (generatedCourt) {
    return translateSymbolWords(key).replace(/^\w/, (char) => char.toUpperCase());
  }

  const translated = translateSymbolWords(label);
  return translated.replace(/^\w/, (char) => char.toUpperCase());
}

export function translateSymbolMeaning(label = "", meaning = "") {
  if (!isSpanishLocale()) return meaning;
  const key = `${symbolKey(label)}|${symbolKey(meaning)}`;
  if (SYMBOL_MEANING_ES[key]) return SYMBOL_MEANING_ES[key];

  const translatedLabel = translateSymbolLabel(label).toLowerCase();
  const original = `${label} ${meaning}`.toLowerCase();

  if (original.includes("relationship") || original.includes("choice")) {
    return `Este simbolo presenta ${translatedLabel} como una senal de eleccion, vinculo y equilibrio entre dos verdades.`;
  }
  if (original.includes("growth") || original.includes("effort")) {
    return `Este simbolo muestra ${translatedLabel} como una forma de crecimiento visible, donde la energia empieza a tomar direccion.`;
  }
  if (original.includes("stability") || original.includes("structure")) {
    return `Este simbolo usa ${translatedLabel} para hablar de estabilidad, pausa y estructura dentro de la lectura.`;
  }
  if (original.includes("tension") || original.includes("challenge")) {
    return `Este simbolo marca ${translatedLabel} como una tension que pide atencion antes de avanzar.`;
  }
  if (original.includes("balance") || original.includes("repaired")) {
    return `Este simbolo muestra ${translatedLabel} como una senal de equilibrio, reparacion y apoyo que puede recuperarse.`;
  }
  if (original.includes("discernment") || original.includes("truth")) {
    return `Este simbolo convierte ${translatedLabel} en una invitacion a mirar con discernimiento y decirte la verdad con calma.`;
  }
  if (original.includes("momentum") || original.includes("motion")) {
    return `Este simbolo presenta ${translatedLabel} como movimiento: energia que necesita direccion, ritmo y una accion concreta.`;
  }
  if (original.includes("completion") || original.includes("consequence")) {
    return `Este simbolo muestra ${translatedLabel} como cierre de ciclo, consecuencia y una verdad que ya no se puede evitar.`;
  }
  if (original.includes("beginner") || original.includes("curiosity")) {
    return `Este simbolo presenta ${translatedLabel} como aprendizaje, curiosidad y apertura para practicar sin exigir perfeccion.`;
  }
  if (original.includes("authority") || original.includes("responsibility")) {
    return `Este simbolo muestra ${translatedLabel} como madurez, responsabilidad y capacidad de sostener una decision.`;
  }

  return `Este simbolo destaca ${translatedLabel} como una clave visual de la carta. Observa que emocion despierta y como cambia el tono de la lectura.`;
}

export function spanishCardOverview(card, uprightSummary = "", reversedSummary = "") {
  const name = translateCardName(card?.name);
  const suit = translateSuitName(card?.suit);
  const theme = translateKeywordPhrase(card?.subtitle || "").toLowerCase();
  const upright = uprightSummary ? `Al derecho, invita a mirar ${uprightSummary.toLowerCase()}.` : "";
  const reversed = reversedSummary ? `Invertida, puede señalar ${reversedSummary.toLowerCase()}.` : "";

  return `${name} pertenece a ${suit} y abre una lectura centrada en ${theme || "claridad interior"}. Esta carta no busca darte una respuesta rígida, sino ayudarte a escuchar lo que ya se mueve dentro de ti y convertirlo en una decisión más consciente. ${upright} ${reversed} Léela como un espejo suave: observa la imagen, nombra lo que sientes y deja que el mensaje se traduzca en un paso concreto para hoy.`;
}

export function spanishCardReading(card) {
  const name = translateCardName(card?.name);
  const suit = translateSuitName(card?.suit);
  const theme = translateKeywordPhrase(card?.subtitle || "").toLowerCase();

  return `Cuando aparece ${name}, la lectura te pide presencia. ${suit} aporta el tono de la experiencia y orienta la pregunta hacia ${theme || "lo que necesita atención ahora"}.

No tienes que forzar una conclusión inmediata. Mira primero lo que la carta despierta en ti: una emoción, una resistencia, una esperanza o una decisión que ya estaba esperando nombre. Después vuelve al contexto de tu pregunta y distingue entre impulso, miedo y verdad.

Como consejo práctico, elige una sola acción pequeña que honre el mensaje de ${name}. Puede ser una conversación honesta, un límite, una pausa, una disculpa, una decisión pendiente o un gesto de cuidado hacia ti. La lectura se vuelve más clara cuando la conviertes en algo que puedas vivir.`;
}

export function spanishLifeMeaning(card, area) {
  const name = translateCardName(card?.name);
  const theme = translateKeywordPhrase(card?.subtitle || "").toLowerCase();
  const areaCopy = {
    love: `En el amor, ${name} invita a mirar la relación con honestidad emocional. Observa qué necesita cuidado, qué necesita un límite y qué deseo auténtico está buscando espacio. El tema central es ${theme || "claridad y presencia"}.`,
    career: `En el trabajo, ${name} pide convertir intuición en una decisión práctica. Revisa prioridades, recursos y conversaciones pendientes. El mensaje no es correr, sino elegir el siguiente paso con más conciencia.`,
    spirit: `En lo espiritual, ${name} abre una pausa para escuchar tu mundo interior. Quédate con lo que se repite en sueños, emociones o señales sutiles, y deja que esa información te ayude a volver a tu centro.`,
  };

  return areaCopy[area] || "";
}

export function spanishShadowMessage(card) {
  const name = translateCardName(card?.name);
  return `${name} puede señalar una zona que prefieres evitar: una emoción no dicha, una decisión aplazada o una verdad que necesita más cuidado. La invitación no es juzgarte, sino mirar con calma qué patrón pide atención.`;
}

export function spanishJournalPrompts(card) {
  const name = translateCardName(card?.name);
  return [
    `¿Qué parte de mi situación refleja ${name} con más claridad?`,
    "¿Qué estoy sintiendo antes de intentar explicarlo?",
    "¿Cuál es el paso más pequeño y honesto que puedo dar hoy?",
  ];
}

export const COMMON_COPY_ES = {
  allCards: "Todas las cartas",
  allTarotCards: "Todas las cartas del tarot",
  askYourself: "Pregúntate",
  breadcrumb: "Ruta de navegación",
  browseAllCards: "Revisa todas las cartas.",
  career: "Trabajo",
  cardsToReadWith: "Cartas para leer junto a esta",
  checkUrl: "Revisa la dirección o explora todas las cartas.",
  emotionalThemes: "Temas emocionales",
  faq: "Preguntas frecuentes",
  hiddenChallenge: "Desafío oculto",
  howToRead: "Cómo leer esta carta",
  love: "Amor",
  meaning: "Significado",
  noResults: "No se encontraron resultados.",
  realLife: "En la vida real",
  reversed: "Significado invertido",
  searchDescription:
    "Busca en la biblioteca de cartas de TarotFate y abre significados completos al derecho e invertidos.",
  searchEmptyTitle: "Buscar cartas del tarot | TarotFate",
  searchResultsFor: "Resultados de búsqueda para",
  spirit: "Espíritu",
  symbols: "Símbolos",
  tarotCardNotFound: "Carta de tarot no encontrada",
  upright: "Significado al derecho",
};
